/**
 * Utilitário de Formatação WhatsApp
 *
 * O que faz: Converte texto com formatação WhatsApp em React elements
 * - *texto* → negrito
 * - _texto_ → itálico  
 * - ~texto~ → riscado
 * - Preserva quebras de linha
 *
 * Onde usa: ChatWindow.tsx para renderizar mensagens formatadas
 */

import React from 'react';

/**
 * Parseia texto com formatação WhatsApp e retorna elementos React
 * @param text - Texto bruto com possível formatação WhatsApp
 * @returns Array de elementos React ou strings
 */
export function formatWhatsAppText(text: string): React.ReactNode {
    if (!text) return null;

    // Primeiro, divide por quebras de linha para preservá-las
    const lines = text.split('\n');

    return lines.map((line, lineIndex) => {
        // Se linha vazia, retorna <br>
        if (line === '') {
            return <br key={`br-${lineIndex}`} />;
        }

        // Regex combinada: WhatsApp formatting + URLs
        const regex = /(\*[^*]+\*)|(_[^_]+_)|(~[^~]+~)|(https?:\/\/[^\s<>"{}|\\^[\]`]+)/g;

        let lastIndex = 0;
        const elements: React.ReactNode[] = [];
        let match: RegExpExecArray | null;
        let elementIndex = 0;

        while ((match = regex.exec(line)) !== null) {
            // Adiciona texto antes do match
            if (match.index > lastIndex) {
                elements.push(line.substring(lastIndex, match.index));
            }

            const matched = match[0];
            const content = matched.substring(1, matched.length - 1);

            // Determina o tipo de formatação
            if (matched.startsWith('*')) {
                elements.push(
                    <strong key={`bold-${lineIndex}-${elementIndex}`}>{content}</strong>
                );
            } else if (matched.startsWith('_')) {
                elements.push(
                    <em key={`italic-${lineIndex}-${elementIndex}`}>{content}</em>
                );
            } else if (matched.startsWith('~')) {
                elements.push(
                    <del key={`strike-${lineIndex}-${elementIndex}`}>{content}</del>
                );
            } else if (matched.startsWith('http://') || matched.startsWith('https://')) {
                elements.push(
                    <a
                        key={`url-${lineIndex}-${elementIndex}`}
                        href={matched}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline opacity-90 hover:opacity-100 break-all"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {matched}
                    </a>
                );
            }

            lastIndex = regex.lastIndex;
            elementIndex++;
        }

        // Adiciona texto restante após último match
        if (lastIndex < line.length) {
            elements.push(line.substring(lastIndex));
        }

        // Se não há elementos, retorna a linha original
        if (elements.length === 0) {
            elements.push(line);
        }

        // Retorna span com key única para cada linha
        // Adiciona <br> entre linhas (exceto a última)
        return (
            <React.Fragment key={`line-${lineIndex}`}>
                <span>{elements}</span>
                {lineIndex < lines.length - 1 && <br />}
            </React.Fragment>
        );
    });
}

/** Extrai URLs de um texto */
export function extractUrls(text: string): string[] {
    if (!text) return [];
    const urlRegex = /https?:\/\/[^\s<>"{}|\\^[\]`]+/g;
    return text.match(urlRegex) || [];
}

export default formatWhatsAppText;
